import { useNavigate } from "react-router-dom";
import { useCallback, useMemo, useRef, useState } from "react";

import BaseAvatar from "@/components_v2/Avatar/BaseAvatar";
import { refreshAuthSession } from "@/redux/api";
import { UserAvatarProps } from "@/domains/users/types";

const MAX_IMAGE_RETRIES = 3;
const DELETED_USER_LABEL = "?";

const UserAvatar = ({
  user,
  shouldClickRedirect = false,
  ...props
}: UserAvatarProps) => {
  const navigate = useNavigate();
  const [retryToken, setRetryToken] = useState(0);
  const retries = useRef(0);

  const label = useMemo(() => {
    if (!user) return DELETED_USER_LABEL;
    if (Boolean(user.firstName?.trim()) && Boolean(user.lastName?.trim())) {
      return (user.firstName.charAt(0) + user.lastName.charAt(0)).toUpperCase();
    }
    return user.fullName.slice(0, 2).toUpperCase();
  }, [user]);

  const handleImageError = useCallback(() => {
    if (!user?.profilePicture || retries.current >= MAX_IMAGE_RETRIES) return;

    retries.current += 1;
    refreshAuthSession()
      .catch(() => {})
      .finally(() => setRetryToken((token) => token + 1));
  }, [user?.profilePicture]);

  return (
    <BaseAvatar
      key={retryToken}
      className="base-user-avatar"
      label={label}
      onClick={
        shouldClickRedirect && user
          ? () => navigate(`/users/${user.id}`)
          : undefined
      }
      style={shouldClickRedirect ? { cursor: "pointer" } : {}}
      {...props}
      imageUrl={user?.profilePicture}
      onImageError={handleImageError}
    />
  );
};

export default UserAvatar;
